import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, useTheme, Divider } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from '../../MiniBot';
import { useAuth } from '../../../helpers/AuthProvider';

const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
        </Box>
    );
};

interface SolutionOwnerData {
    id: string;
    name: string;
}

interface DataSourceDetails {
    projectId: string;
    location: string;
    lake: string;
    zone: string;
    description?: string;
    resourceSpec: {
        type: string;
        name: string;
    };
    discoverySpec: {
        enabled: boolean;
        csvOptions?: {
            headerRows: number;
            delimiter: string;
        };
    };
}

interface DataSourceData {
    id: string;
    name: string;
    solutionType: string;
    solutionCategoryType: string;
    reasonForRejection: string;
    cloudProvider: 'gcp' | 'aws' | 'azure' | string;
    details: {
        dataSources: DataSourceDetails;
    };
    status: string;
    solutionOwner: SolutionOwnerData;
}

const DataSourcesDetailsPageContent: React.FC = () => {
    const navigate = useNavigate();
    const {id}  = useParams();
    const [dataSource, setDataSource] = useState<DataSourceData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();
    const [dynamicLabels, setDynamicLabels] = useState({
        projectId: 'Project ID',
    });

    useEffect(() => {
        const fetchDataSourceData = async () => {
            if (!id) {
                setIsLoading(false);
                setError("Data Source ID is missing from URL.");
                return;
            }

            setIsLoading(true);
            setError(null);
            try {
                const query = `
                    query GetDataSource($getDataSourceId: String!) {
                      getDataSource(id: $getDataSourceId) {
                        id
                        name
                        solutionType
                        solutionCategoryType
                        reasonForRejection
                        cloudProvider
                        status
                        details {
                          dataSources {
                            projectId
                            location
                            lake
                            zone
                            description
                            resourceSpec {
                              type
                              name
                            }
                            discoverySpec {
                              enabled
                              csvOptions {
                                headerRows
                                delimiter
                              }
                            }
                          }
                        }
                        solutionOwner {
                          id
                          name
                        }
                      }
                    }
                `;
                const response = await queryGraphql(query, { getDataSourceId: id });
                
                if (response.getDataSource) {
                    setDataSource(response.getDataSource);
                } else if (response.errors && response.errors.length > 0) {
                    setError(response.errors[0].message || "Error fetching data.");
                    setDataSource(null);
                } else {
                    setError("Data Source not found or unexpected response structure.");
                    setDataSource(null);
                }
            } catch (err: any) {
                console.error("Error fetching Data Source data:", err);
                setError(err.message || "Failed to fetch Data Source details.");
                setDataSource(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDataSourceData();
    }, [id]);
    
    useEffect(() => {
        if (dataSource?.cloudProvider) {
            switch (dataSource.cloudProvider) {
                case 'aws':
                    setDynamicLabels({
                        projectId: 'Account ID',
                    });
                    break;
                case 'azure':
                    setDynamicLabels({
                        projectId: 'Subscription ID',
                    });
                    break;
                case 'gcp':
                default:
                    setDynamicLabels({
                        projectId: 'Project ID',
                    });
                    break;
            }
        }
    }, [dataSource]);


    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>Loading data source details...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography color="error" sx={{p:2}}>{error}</Typography>;
    }

    if (!dataSource) {
        return <Typography sx={{p:2}}>No data source data available.</Typography>;
    }

    const resourceOwnerName = dataSource.solutionOwner.name;
    const dsDetails = dataSource.details.dataSources; // Helper variable

    const getStatusBackgroundColor = (status: string | undefined): string => {
        if (!status) return "#F8F8F8";
        const lowerStatus = status.toLowerCase();
        if (lowerStatus === "active" || lowerStatus === "approved") return "#12B76A10";
        if (lowerStatus === "rejected") return "#F0443810";
        if (lowerStatus === "pending") return "#FDB02210";
        return "#F8F8F8";
    };

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/datasolutions/data-sources" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/datasolutions/data-sources'); }}> Data Sources </Link>
                <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Data Source Details</Typography>
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/datasolutions/data-sources')}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Data Source Details </Typography>
            </Box>

            <Box sx={{mx:0, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
                <Box sx={{
                    p: 2.5,
                    bgcolor: getStatusBackgroundColor(dataSource.status),
                    mb: 0,
                    mx: 0,
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
                                {dataSource.name}
                            </Typography>
                            {user.roles?.some((e: string) => ["cluster_owner"].includes(e)) && dataSource.status === "pending" && (
                                <IconButton sx={{ p: 0.5 }} edge="end" aria-label="edit" color="inherit" onClick={() => navigate(`/datasolutions/data-sources/edit/${id}`)}>
                                    <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
                                </IconButton>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
                            Resource Owner: {resourceOwnerName}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={3} sx={{p:3, pt: 2.5 }}>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>{dynamicLabels.projectId}</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dsDetails.projectId || 'N/A'}</Typography>
                    </Grid>
                     <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Location</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dsDetails.location || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Lake</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dsDetails.lake || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Zone</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dsDetails.zone || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Cloud Provider</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dataSource.cloudProvider?.toUpperCase() || 'N/A'}</Typography>
                    </Grid>
                </Grid>
            </Box>

              {dataSource.status === 'rejected' && (
                    <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow="0px 3px 6px #10101029">
                        <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                        <Divider sx={{ marginY: 2 }} />
                        <DetailItem label="Reason for Rejection" value={dataSource.reasonForRejection} />
                    </Box>
                )}

                {dataSource.status === 'active' && (
                    <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow="0px 3px 6px #10101029">
                        <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Data Source Specifications</Typography>
                        <Divider sx={{ marginY: 2 }} />
                        <Grid container spacing={2.5}>
                            <Grid item xs={12}><DetailItem label="Description" value={dsDetails.description} /></Grid>
                            <Grid item xs={12} sm={6} md={3}><DetailItem label="Resource Type" value={dsDetails.resourceSpec?.type} /></Grid>
                            <Grid item xs={12} sm={6} md={9}><DetailItem label="Resource Name" value={dsDetails.resourceSpec?.name} /></Grid>
                            
                            <Grid item xs={12}><Divider sx={{mt: 1, mb: -1}} /></Grid>

                            <Grid item xs={12} sm={6} md={3}><DetailItem label="Discovery" value={dsDetails.discoverySpec?.enabled} /></Grid>
                            {dsDetails.discoverySpec?.enabled && (
                                <>
                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="CSV Header Rows" value={dsDetails.discoverySpec.csvOptions?.headerRows} /></Grid>
                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="CSV Delimiter" value={dsDetails.discoverySpec.csvOptions?.delimiter} /></Grid>
                                </>
                            )}
                        </Grid>
                    </Box>
                )}
        </Container>
    );
};


const DataSourcesDetailsPage: React.FC = () => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };

    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                index={"datasolutions-data-sources"}
                onStateChange={handleDrawerStateChange}
            />
            <MiniBot/>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: {xs: 0, sm: `${currentMarginLeft}px`},
                }}
            >
                <DataSourcesDetailsPageContent />
            </Box>
        </Box>
    );
};

export default DataSourcesDetailsPage;



// import React, { useState, useEffect } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, useTheme, Divider } from '@mui/material';
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";
// import queryGraphql from "../../../helpers/ApiInterface";
// import MiniBot from '../../MiniBot';
// import { useAuth } from '../../../helpers/AuthProvider';

// const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
//     const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
//     return (
//         <Box mb={2}>
//             <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
//             <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
//         </Box>
//     );
// };
// // --- Interfaces for Data Source ---
// interface ApplicationData {
//     id: string;
//     applicationName: string;
// }

// interface SolutionOwnerData {
//     id: string;
//     name: string;
// }

// interface DataSourceDetails {
//     projectId: string;
//     location: string;
//     lake: string;
//     zone: string;
//     zoneType: string;
//     description?: string;
//     resourceSpec: {
//         type: string;
//         name: string;
//     };
//     discoverySpec: {
//         enabled: boolean;
//         csvOptions?: {
//             headerRows: number;
//             delimiter: string;
//         };
//     };
// }

// interface DataSourceData {
//     id: string;
//     name: string;
//     solutionType: string;
//     solutionCategoryType: string;
//     reasonForRejection: string;
//     cloudProvider: 'gcp' | 'aws' | 'azure' | string;
//     environment: string;
//     details: {
//         dataSources: DataSourceDetails; // Updated key
//     };
//     status: string;
//     solutionOwner: SolutionOwnerData;
//     application?: ApplicationData;
// }

// const DataSourcesDetailsPageContent: React.FC = () => {
//     const navigate = useNavigate();
//     const {id}  = useParams();
//     const [dataSource, setDataSource] = useState<DataSourceData | null>(null);
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const { user } = useAuth();
//     const [dynamicLabels, setDynamicLabels] = useState({
//         projectId: 'Project ID',
//     });

//     useEffect(() => {
//         const fetchDataSourceData = async () => {
//             if (!id) {
//                 setIsLoading(false);
//                 setError("Data Source ID is missing from URL.");
//                 return;
//             }

//             setIsLoading(true);
//             setError(null);
//             try {
//                 const query = `
//                     query GetDataSource($getDataSourceId: String!) {
//                       getDataSource(id: $getDataSourceId) {
//                         id
//                         name
//                         solutionType
//                         solutionCategoryType
//                         reasonForRejection
//                         cloudProvider
//                         environment
//                         status
//                         details {
//                           dataSources {
//                             projectId
//                             location
//                             lake
//                             zone
//                             zoneType
//                             description
//                             resourceSpec {
//                               type
//                               name
//                             }
//                             discoverySpec {
//                               enabled
//                               csvOptions {
//                                 headerRows
//                                 delimiter
//                               }
//                             }
//                           }
//                         }
//                         solutionOwner {
//                           id
//                           name
//                         }
//                         application {
//                           id
//                           applicationName
//                         }
//                       }
//                     }
//                 `;
//                 const response = await queryGraphql(query, { getDataSourceId: id });
                
//                 if (response.getDataSource) {
//                     setDataSource(response.getDataSource);
//                 } else if (response.errors && response.errors.length > 0) {
//                     setError(response.errors[0].message || "Error fetching data.");
//                     setDataSource(null);
//                 } else {
//                     setError("Data Source not found or unexpected response structure.");
//                     setDataSource(null);
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Data Source data:", err);
//                 setError(err.message || "Failed to fetch Data Source details.");
//                 setDataSource(null);
//             } finally {
//                 setIsLoading(false);
//             }
//         };

//         fetchDataSourceData();
//     }, [id]);
    
//     useEffect(() => {
//         if (dataSource?.cloudProvider) {
//             switch (dataSource.cloudProvider) {
//                 case 'aws':
//                     setDynamicLabels({
//                         projectId: 'Account ID',
//                     });
//                     break;
//                 case 'azure':
//                     setDynamicLabels({
//                         projectId: 'Subscription ID',
//                     });
//                     break;
//                 case 'gcp':
//                 default:
//                     setDynamicLabels({
//                         projectId: 'Project ID',
//                     });
//                     break;
//             }
//         }
//     }, [dataSource]);


//     if (isLoading) {
//         return (
//             <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
//                 <CircularProgress size={24} />
//                 <Typography sx={{ ml: 2 }}>Loading data source details...</Typography>
//             </Box>
//         );
//     }

//     if (error) {
//         return <Typography color="error" sx={{p:2}}>{error}</Typography>;
//     }

//     if (!dataSource) {
//         return <Typography sx={{p:2}}>No data source data available.</Typography>;
//     }

//     const resourceOwnerName = dataSource.solutionOwner.name;
//     const dsDetails = dataSource.details.dataSources; // Helper variable

//     const getStatusBackgroundColor = (status: string | undefined): string => {
//         if (!status) return "#F8F8F8";
//         const lowerStatus = status.toLowerCase();
//         if (lowerStatus === "active" || lowerStatus === "approved") return "#12B76A10";
//         if (lowerStatus === "rejected") return "#F0443810";
//         if (lowerStatus === "pending") return "#FDB02210";
//         return "#F8F8F8";
//     };

//     return (
//         <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
//             <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
//                 <Link underline="hover" color="inherit" href="/datasolutions/data-sources" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/datasolutions/data-sources'); }}> Data Sources </Link>
//                 <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Data Source Details</Typography>
//             </Breadcrumbs>

//             <Box display="flex" alignItems="center" mb={3}>
//                 <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/datasolutions/data-sources')}>
//                     <ArrowBackIosNewIcon fontSize="small" />
//                 </IconButton>
//                 <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Data Source Details </Typography>
//             </Box>

//             <Box sx={{mx:0, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
//                 <Box sx={{
//                     p: 2.5,
//                     bgcolor: getStatusBackgroundColor(dataSource.status),
//                     mb: 0,
//                     mx: 0,
//                     borderTopLeftRadius: "8px",
//                     borderTopRightRadius: "8px",
//                 }}>
//                     <Box>
//                         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
//                             <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
//                                 {dataSource.name}
//                             </Typography>
//                             {user.roles?.some((e: string) => ["cluster_owner"].includes(e)) && dataSource.status === "pending" && (
//                                 <IconButton sx={{ p: 0.5 }} edge="end" aria-label="edit" color="inherit" onClick={() => navigate(`/datasolutions/data-sources/edit/${id}`)}>
//                                     <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
//                                 </IconButton>
//                             )}
//                         </Box>
//                         <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
//                             Resource Owner: {resourceOwnerName}
//                         </Typography>
//                     </Box>
//                 </Box>

//                 <Grid container spacing={3} sx={{p:3, pt: 2.5 }}>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>{dynamicLabels.projectId}</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dsDetails.projectId || 'N/A'}</Typography>
//                     </Grid>
//                      <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Location</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dsDetails.location || 'N/A'}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Lake</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dsDetails.lake || 'N/A'}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Zone</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dsDetails.zone || 'N/A'}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Zone Type</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dsDetails.zoneType || 'N/A'}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Application Name</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dataSource.application?.applicationName || 'N/A'}</Typography>
//                     </Grid>
//                     <Grid item xs={12} sm={4} md={3}>
//                         <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Cloud Provider</Typography>
//                         <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dataSource.cloudProvider?.toUpperCase() || 'N/A'}</Typography>
//                     </Grid>
//                 </Grid>
//             </Box>

//               {dataSource.status === 'rejected' && (
//                     <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow="0px 3px 6px #10101029">
//                         <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
//                         <Divider sx={{ marginY: 2 }} />
//                         <DetailItem label="Reason for Rejection" value={dataSource.reasonForRejection} />
//                     </Box>
//                 )}

//                 {dataSource.status === 'active' && (
//                     <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow="0px 3px 6px #10101029">
//                         <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Data Source Specifications</Typography>
//                         <Divider sx={{ marginY: 2 }} />
//                         <Grid container spacing={2.5}>
//                             <Grid item xs={12}><DetailItem label="Description" value={dsDetails.description} /></Grid>
//                             <Grid item xs={12} sm={6} md={3}><DetailItem label="Resource Type" value={dsDetails.resourceSpec?.type} /></Grid>
//                             <Grid item xs={12} sm={6} md={9}><DetailItem label="Resource Name" value={dsDetails.resourceSpec?.name} /></Grid>
                            
//                             <Grid item xs={12}><Divider sx={{mt: 1, mb: -1}} /></Grid>

//                             <Grid item xs={12} sm={6} md={3}><DetailItem label="Discovery" value={dsDetails.discoverySpec?.enabled} /></Grid>
//                             {dsDetails.discoverySpec?.enabled && (
//                                 <>
//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="CSV Header Rows" value={dsDetails.discoverySpec.csvOptions?.headerRows} /></Grid>
//                                     <Grid item xs={12} sm={6} md={3}><DetailItem label="CSV Delimiter" value={dsDetails.discoverySpec.csvOptions?.delimiter} /></Grid>
//                                 </>
//                             )}
//                         </Grid>
//                     </Box>
//                 )}
//         </Container>
//     );
// };


// const DataSourcesDetailsPage: React.FC = () => {
//     const theme = useTheme();
//     const getInitialDrawerState = () => {
//         if (typeof window !== 'undefined') {
//             const storedState = localStorage.getItem('isFaradayDrawerOpen');
//             return storedState !== null ? JSON.parse(storedState) : true;
//         }
//         return true;
//     };
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
//         setIsDrawerActuallyOpen(isOpenFromDrawer);
//         if (typeof window !== 'undefined') {
//             localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
//         }
//     };

//     const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer
//                 index={"datasolutions-data-sources"}
//                 onStateChange={handleDrawerStateChange}
//             />
//             <MiniBot/>
//             <Box
//                 component="main"
//                 sx={{
//                     flexGrow: 1,
//                     p: 0,
//                     transition: theme.transitions.create('margin', {
//                         easing: theme.transitions.easing.sharp,
//                         duration: theme.transitions.duration.enteringScreen,
//                     }),
//                     marginLeft: {xs: 0, sm: `${currentMarginLeft}px`},
//                 }}
//             >
//                 <DataSourcesDetailsPageContent />
//             </Box>
//         </Box>
//     );
// };

// export default DataSourcesDetailsPage;